import React from 'react';
import { PopupWrapperView } from '@src/features/order/pages/orders-page/components';
import { NewPopupProps } from './types';
import { ArcStack, ArcCellInfo } from '@src/core/ui';
import { CancelNewOrderButton, ProceedButton, UpdateNewOrderButton } from '@src/features/order/pages/orders-page/actions';
import { OrderEntity, OrderWayPointEntity } from '@src/features/order/entities';
import { formatDeliveryMode } from '@src/features/order/utils';
import { useGetUserDataQuery } from '@src/core/features';
import { useUnitType } from '@src/core/features/settings';
import { arcDateWithTime, arcFormatCoordinateDisplay, arcSpatium, arcVelocitas } from '@src/core/extensions';
import { EMPTY_STRING } from '@src/core/config';
import OrdersMap from '@src/features/order/pages/orders-page/map';
import { ArcGoogleMap } from '@src/core/controllers';

const isHomeWaypoint = (w: OrderWayPointEntity): boolean => w.type === 'HOME';

const NewPopup: React.FC<NewPopupProps> = (props) => {
  const { order = {} as OrderEntity } = props;
  // Re-render on a live unit switch — altitude/speed values are formatted via arcSpatium/arcVelocitas.
  useUnitType();
  const { data: userData } = useGetUserDataQuery();

  const takeoff = order.takeoffLocation;
  const destination = order.deliveryLocation;

  const waypoints = order.waypoints ?? [];
  const routeWaypoints = waypoints.some(isHomeWaypoint)
    ? waypoints.filter((w) => !isHomeWaypoint(w))
    : waypoints.slice(1);
  const takeOffWp = routeWaypoints[0];
  const orderWp = routeWaypoints.find((w) => Boolean(w.orderId)) ?? routeWaypoints[routeWaypoints.length - 1];

  const distance = (value?: string | number): string =>
    value != null && Number.isFinite(Number(value)) ? arcSpatium(Number(value), { fromSi: true }) : EMPTY_STRING;
  const speed = (value?: string | number): string =>
    value != null && Number.isFinite(Number(value)) ? arcVelocitas(Number(value), { fromSi: true }) : EMPTY_STRING;

  return (
    <PopupWrapperView title={'MISSION DETAILS'}>
      <ArcStack direction='row' spacing={2} width='100%'>
        <ArcStack flex={1} minWidth={0}>
          <ArcStack my={1} spacing={2}>
            <ArcStack direction='row' spacing={1}>
              <ArcCellInfo label='Order' width='50%' textProps={{ fontWeight: 400 }}>#{order.id ?? 'N/A'}</ArcCellInfo>
              <ArcCellInfo label='Date' width='50%' textProps={{ fontWeight: 400 }}>
                {order.createDate ? arcDateWithTime(String(order.createDate)) : EMPTY_STRING}
              </ArcCellInfo>
            </ArcStack>

            <ArcCellInfo label='Takeoff Location' width='100%' textProps={{ fontWeight: 400 }}>
              {takeoff?.address || EMPTY_STRING}
            </ArcCellInfo>

            <ArcStack direction='row' spacing={1}>
              <ArcCellInfo label='Takeoff Latitude' width='50%' textProps={{ fontWeight: 400 }}>
                {arcFormatCoordinateDisplay(takeoff?.latitude, EMPTY_STRING)}
              </ArcCellInfo>
              <ArcCellInfo label='Takeoff Longitude' width='50%' textProps={{ fontWeight: 400 }}>
                {arcFormatCoordinateDisplay(takeoff?.longitude, EMPTY_STRING)}
              </ArcCellInfo>
            </ArcStack>

            <ArcCellInfo label='Destination Location' width='100%' textProps={{ fontWeight: 400 }}>
              {destination?.address || EMPTY_STRING}
            </ArcCellInfo>

            <ArcStack direction='row' spacing={1}>
              <ArcCellInfo label='Destination Latitude' width='50%' textProps={{ fontWeight: 400 }}>
                {arcFormatCoordinateDisplay(destination?.latitude, EMPTY_STRING)}
              </ArcCellInfo>
              <ArcCellInfo label='Destination Longitude' width='50%' textProps={{ fontWeight: 400 }}>
                {arcFormatCoordinateDisplay(destination?.longitude, EMPTY_STRING)}
              </ArcCellInfo>
            </ArcStack>

            <ArcStack direction='row' spacing={1}>
              <ArcCellInfo label='Takeoff Altitude' width='50%' textProps={{ fontWeight: 400 }}>
                {distance(takeOffWp?.altitude ?? takeoff?.altitude)}
              </ArcCellInfo>
              <ArcCellInfo label='Destination Altitude' width='50%' textProps={{ fontWeight: 400 }}>
                {distance(orderWp?.altitude ?? destination?.altitude)}
              </ArcCellInfo>
            </ArcStack>

            <ArcStack direction='row' spacing={1}>
              <ArcCellInfo label='Delivery Altitude' width='50%' textProps={{ fontWeight: 400 }}>
                {distance(orderWp?.deliveryAltitude)}
              </ArcCellInfo>
              <ArcCellInfo label='Cruise Speed' width='50%' textProps={{ fontWeight: 400 }}>
                {speed(orderWp?.speed ?? order.speed)}
              </ArcCellInfo>
            </ArcStack>

            <ArcStack direction='row' spacing={1}>
              <ArcCellInfo label='Delivery Mode' width='50%' textProps={{ fontWeight: 400 }}>
                {formatDeliveryMode(order.deliveryMode) || EMPTY_STRING}
              </ArcCellInfo>
              <ArcCellInfo label='HUB' width='50%' textProps={{ fontWeight: 400 }}>
                {userData?.hub?.name || takeoff?.address || EMPTY_STRING}
              </ArcCellInfo>
            </ArcStack>

            <ArcStack direction='row' spacing={1}>
              <ArcCellInfo label='Package Content' width='50%' sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }} textProps={{ fontWeight: 400 }}>
                {order.packageContent || EMPTY_STRING}
              </ArcCellInfo>
              <ArcCellInfo label='Client Name' width='50%' sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }} textProps={{ fontWeight: 400 }}>
                {order.clientName || EMPTY_STRING}
              </ArcCellInfo>
            </ArcStack>
          </ArcStack>

          <ArcStack spacing={1} mt={2}>
            <ArcStack spacing={1} direction='row'>
              <CancelNewOrderButton orderId={order.id} />
              <UpdateNewOrderButton order={order} />
            </ArcStack>
            <ProceedButton order={order} />
          </ArcStack>
        </ArcStack>
        <ArcStack width='336px' borderRadius={1} sx={{ overflow: 'hidden' }}>
          <ArcGoogleMap hideToolbar hideMapControls>
            <OrdersMap />
          </ArcGoogleMap>
        </ArcStack>
      </ArcStack>
    </PopupWrapperView>
  );
};

export default NewPopup;
